import type {
  CreditableCompany,
  CreditCompanyRequest,
  CreditCompanyResult,
} from '@nestjs-boilerplate/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { apiClient } from '@/lib/api-client';
import { companyRiskQueryKeys } from './company-risk';
import { transactionQueryKeys } from './transactions';

export type { CreditableCompany, CreditCompanyResult } from '@nestjs-boilerplate/types';

export const companyCreditKeys = {
  all: ['admin', 'companies', 'creditable'] as const,
};

/** Fetches every firm with its current pooled balance. */
export async function getCreditableCompanies(): Promise<CreditableCompany[]> {
  return apiClient.fetch<CreditableCompany[]>('/admin/companies/creditable');
}

/**
 * The creditable list, fetched only when the caller may have it.
 *
 * The endpoint is guarded by `admin:billing`. An administrator holding only
 * `admin:cases` has a legitimate reason to be on the companies screen, and
 * firing this for them would produce a 403 per mount for a column they are not
 * shown anyway.
 *
 * @param enabled - Whether the caller holds `admin:billing`
 */
export function useCreditableCompanies(enabled = true) {
  return useQuery({
    queryKey: companyCreditKeys.all,
    queryFn: getCreditableCompanies,
    enabled,
  });
}

/**
 * Adds credit to a firm's shared pool.
 *
 * On success both the company list and the transaction history are invalidated:
 * the credit changes a balance *and* writes a ledger line, so leaving the
 * history stale would show an administrator a pool that had grown with nothing
 * on the transactions screen explaining it.
 */
export function useCreditCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ companyId, ...body }: CreditCompanyRequest & { companyId: string }) =>
      apiClient.fetch<CreditCompanyResult>(`/admin/companies/${companyId}/credit`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: companyCreditKeys.all });
      queryClient.invalidateQueries({ queryKey: transactionQueryKeys.all });
    },
  });
}

/**
 * Removes a company that has no users and holds no balance.
 *
 * The API refuses anything else, so a 409 here is the server declining rather
 * than a bug — surfaced as its own message because "still has 2 users" and
 * "holds £25.00" are both things the administrator can act on.
 */
export function useDeleteCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (companyId: string) =>
      apiClient.fetch<{ id: string }>(`/admin/companies/${companyId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: companyCreditKeys.all });
      // The risk list is the companies table's spine — the credit query only
      // joins the billing side onto it. Invalidating just the latter leaves the
      // deleted firm rendered and clickable until the five-minute global
      // staleTime expires, which reads as a delete that silently failed.
      queryClient.invalidateQueries({ queryKey: companyRiskQueryKeys.all });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete the company');
    },
  });
}
